import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { CaretDown, CaretUp, CheckCircle } from '@phosphor-icons/react';
import { Icon } from '../../../../../components/Icon';
import {
  NeedSomethingNote,
  WaitingForYouCard,
  YourAccountCard,
} from '../../../../../components/projects/client-panels';
import { ProjectCard, ProjectCardSkeleton } from '../../../../../components/projects/project-card';
import { EmptyState } from '../../../../../components/ui/empty-state';
import { InfiniteList } from '../../../../../components/ui/infinite-list';
import { SearchInput } from '../../../../../components/ui/input';
import { useDebouncedValue } from '../../../../../lib/use-debounced-value';
import { projectListQuery } from '../../../../../lib/projects-api';
import { waitingQuery } from '../../../../../lib/tasks-api';
import { NoResults } from '../../../../../components/ui/state-views';

export const Route = createFileRoute('/subdomain/app/_client/projects/')({
  validateSearch: (search: Record<string, unknown>) => ({
    q: typeof search.q === 'string' && search.q ? search.q : undefined,
  }),
  component: ProjectsPage,
});

const GRID = 'grid gap-(--mbm-space-4) sm:grid-cols-2';

/** The cards on the left, the side cards on the right (Figma C03.1: 660 + 397 with a 24px gap). */
const LAYOUT = 'grid items-start gap-(--mbm-space-6) lg:grid-cols-[minmax(0,1fr)_minmax(0,24.8rem)]';

/** Client Projects (Figma C03.1): active projects as cards, finished ones collapsed underneath. */
function ProjectsPage() {
  const { q } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [text, setText] = useState(q ?? '');
  const search = useDebouncedValue(text.trim(), 250) || undefined;
  const [showCompleted, setShowCompleted] = useState(false);

  // The URL carries the (debounced) search so a reload or a shared link lands on the same list.
  if (search !== q) void navigate({ search: { q: search }, replace: true });

  const active = useInfiniteQuery(projectListQuery({ bucket: 'active', q: search }));
  const completed = useInfiniteQuery(projectListQuery({ bucket: 'completed', q: search }));
  const activeItems = active.data?.pages.flatMap((p) => p.items) ?? [];
  const completedItems = completed.data?.pages.flatMap((p) => p.items) ?? [];
  const waiting = useQuery({ ...waitingQuery(), retry: false }).data?.total ?? 0;

  const noneAtAll =
    !active.isLoading && !completed.isLoading && activeItems.length === 0 && completedItems.length === 0;
  const completedCount = `${completedItems.length}${completed.hasNextPage ? '+' : ''}`;

  return (
    <main className="flex flex-col gap-(--mbm-space-6) px-(--mbm-space-4) py-(--mbm-space-6) lg:px-(--mbm-space-9) lg:py-(--mbm-space-7)">
      <header className="flex flex-wrap items-end justify-between gap-(--mbm-space-4)">
        <div className="flex flex-col gap-(--mbm-space-1)">
          <h1 className="text-heading-page text-text-strong">Projects</h1>
          <p className="text-body-compact text-text-secondary">
            {active.isLoading
              ? 'Loading…'
              : activeItems.length === 0 && completedItems.length === 0
                ? 'Nothing here yet'
                : `${activeItems.length}${active.hasNextPage ? '+' : ''} active${
                    waiting > 0 ? ` · ${waiting} ${waiting === 1 ? 'task' : 'tasks'} waiting for you` : ''
                  }`}
          </p>
        </div>
        <SearchInput
          aria-label="Search projects"
          tone="white"
          placeholder="Search projects"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onClear={() => setText('')}
          containerClassName="w-full sm:w-(--mbm-layout-toolbar-search-width)"
        />
      </header>

      {noneAtAll && !active.isError ? (
        search ? (
          <NoResults
            query={search}
            description="Try a different word, or clear the search."
            onClear={() => setText('')}
          />
        ) : (
          <EmptyState
            title="No projects yet"
            description="Your team sets up projects after your First Chai. Posts, reels and website updates you request will show up here."
          />
        )
      ) : (
        <div className={LAYOUT}>
          <div className="flex flex-col gap-(--mbm-space-4)">
            <section aria-labelledby="in-progress" className="flex flex-col gap-(--mbm-space-3)">
              <h2 id="in-progress" className="text-heading-h3 text-text-primary">
                In progress{' '}
                {activeItems.length > 0 ? <span className="text-text-tertiary">{activeItems.length}</span> : null}
              </h2>
              <InfiniteList
                className={GRID}
                items={activeItems}
                getKey={(p) => p.id}
                renderItem={(p) => <ProjectCard project={p} />}
                hasNextPage={active.hasNextPage}
                isFetchingNextPage={active.isFetchingNextPage}
                fetchNextPage={() => void active.fetchNextPage()}
                isLoading={active.isLoading}
                isError={active.isError}
                onRetry={() => void active.refetch()}
                emptyState={<p className="text-body-medium text-text-secondary">Nothing in progress right now.</p>}
                skeletonRow={<ProjectCardSkeleton />}
                skeletonCount={4}
              />
            </section>

            {completedItems.length > 0 ? (
              <section className="flex flex-col gap-(--mbm-space-3)">
                <button
                  type="button"
                  aria-expanded={showCompleted}
                  onClick={() => setShowCompleted((v) => !v)}
                  className="flex items-center gap-(--mbm-space-3) rounded-(--mbm-component-radius-widget) border border-border-line bg-surface-card p-(--mbm-space-4-5) text-left focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
                >
                  <Icon icon={CheckCircle} size="md" />
                  <span className="text-label-large text-text-primary">Completed</span>
                  <span className="flex-1 text-body-small text-text-secondary">
                    {completedCount} {completedItems.length === 1 && !completed.hasNextPage ? 'project' : 'projects'}
                  </span>
                  <Icon icon={showCompleted ? CaretUp : CaretDown} size="md" />
                </button>
                {showCompleted ? (
                  <div className={GRID}>
                    {completedItems.map((p) => (
                      <ProjectCard key={p.id} project={p} />
                    ))}
                  </div>
                ) : null}
              </section>
            ) : null}
          </div>

          <aside className="flex flex-col gap-(--mbm-space-4)">
            <WaitingForYouCard />
            <YourAccountCard />
            <NeedSomethingNote />
          </aside>
        </div>
      )}
    </main>
  );
}
